// components/Orders/OrderLinesTable.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The order-lines table every order DETAIL page shows: #, image, item, quantity,
// unit price, line total. One component in the house `.lk-table` look (pale
// small-caps header, striped rows, no vertical rules) in place of the eight
// hand-built copies the pages carried, each with its own header colour, its own
// number format ("Ush 30,000.00", "30000", "Ush 30,000") and its own alignment.
//
// Rules it encodes, once:
//   - the currency sits in the column heading ("Price (Ush)"), never in the
//     cell, so figures line up on their digits;
//   - counts are centred under their headers, text is left, money is right on
//     tabular digits, and every cell is vertically centred on its row;
//   - the line is shown AS SOLD from the server read (lineLabel, lineUnitPrice,
//     lineTotal, options and specs included); the base fields are only the
//     fallback for an order read that predates them;
//   - a brand line's fixed specs and the buyer's picks render as chips, so the
//     vendor sees exactly what was bought without opening the catalogue;
//   - a vertical with facts of its own (a pharmacy's Rx / age / controlled
//     badges) passes them through `lineExtra(item)`;
//   - an Order together line names the group member who chose it
//     (`groupMember.label`), as the kitchen ticket does.
//
// The selectable, per-item-status table the admin assignment screens use is
// components/Orders/OrderItemsTable.jsx; this one is for reading an order.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Table } from "react-bootstrap";
import { formatAmount } from "../../utilities/orderSummary";

const PLACEHOLDER_IMAGE = "/logo512.png";

const imageOf = (item, placeholder) =>
  item?.image ||
  item?.imageUrl ||
  (item?.productId && typeof item.productId === "object" ? item.productId.imageUrl : null) ||
  placeholder;

const nameOf = (item) =>
  item?.lineLabel ||
  item?.name ||
  (item?.productId && typeof item.productId === "object" ? item.productId.name : null) ||
  "Unknown product";

const qtyOf = (item) => {
  const q = Number(item?.qty ?? item?.quantity);
  return Number.isFinite(q) && q > 0 ? q : 1;
};

const entriesOf = (obj) =>
  obj && typeof obj === "object"
    ? Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== "")
    : [];

/* A small fact beside a line name (a pharmacy's "Rx", "18+", "Controlled").
   Pages used to put these in a Material-UI <Badge bg="...">, which is not a
   Bootstrap badge and drew nothing but the text. */
const TAG_TONES = {
  info: { bg: "#dbeafe", color: "#1e40af", border: "#bfdbfe" },
  warn: { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
  danger: { bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
  neutral: { bg: "#f0f2f5", color: "#6b7c93", border: "#e4e8ef" },
};
export const LineTag = ({ tone = "neutral", children }) => {
  const t = TAG_TONES[tone] || TAG_TONES.neutral;
  return (
    <span
      style={{
        display: "inline-block",
        marginLeft: 6,
        padding: "1px 7px",
        borderRadius: 999,
        fontSize: "var(--lk-text-xs, 11px)",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        lineHeight: 1.5,
        background: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
        verticalAlign: "middle",
      }}
    >
      {children}
    </span>
  );
};

const chip = (tone) => ({
  display: "inline-block",
  fontSize: "var(--lk-text-xs, 11px)",
  lineHeight: 1.4,
  padding: "1px 7px",
  borderRadius: 4,
  border: `1px solid ${tone === "picked" ? "rgba(99,102,241,0.25)" : "#e2e8f0"}`,
  background: tone === "picked" ? "rgba(99,102,241,0.08)" : "#f1f5f9",
  color: tone === "picked" ? "#4338ca" : "#334155",
});

/** Fixed specs (the product's) and picked specs (the buyer's choices). */
const SpecChips = ({ item }) => {
  const fixed = entriesOf(item?.productSpecsSnapshot || item?.specs);
  const picked = entriesOf(item?.selectedSpecs);
  if (!fixed.length && !picked.length) return null;
  const labelFor = (key) =>
    (Array.isArray(item?.specOptions) && item.specOptions.find((o) => o?.fieldKey === key)?.label) || key;
  return (
    <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
      {fixed.map(([k, v]) => (
        <span key={`fx-${k}`} style={chip("fixed")}>
          {labelFor(k)}: {String(v)}
        </span>
      ))}
      {picked.map(([k, v]) => (
        <span key={`pk-${k}`} style={chip("picked")}>
          {labelFor(k)}: {String(v)}
        </span>
      ))}
    </div>
  );
};

const thumb = {
  width: 40,
  height: 40,
  objectFit: "cover",
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  background: "#fff",
  display: "block",
  cursor: "zoom-in",
};

/* The large preview a thumbnail shows on hover, as the restaurant order pages
   always have. It is drawn in a portal at <body>, positioned from the
   thumbnail's rectangle, because the table sits in a frame that clips
   overflow (and scrolls sideways on a narrow screen): a preview drawn inside
   the cell, the old way, would be cut off at the frame edge. It opens to the
   right of the thumbnail and flips to the left when there is no room. */
const PREVIEW = 220;
const HoverPreview = ({ src, anchor }) => {
  if (!src || !anchor || typeof document === "undefined") return null;
  const gap = 12;
  const fitsRight = anchor.right + gap + PREVIEW < window.innerWidth;
  const left = fitsRight ? anchor.right + gap : Math.max(8, anchor.left - gap - PREVIEW);
  const top = Math.min(
    Math.max(8, anchor.top + anchor.height / 2 - PREVIEW / 2),
    window.innerHeight - PREVIEW - 8
  );
  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        top,
        left,
        width: PREVIEW,
        height: PREVIEW,
        padding: 4,
        background: "#fff",
        border: "2px solid #f2c811",
        borderRadius: 10,
        boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
        zIndex: 2000,
        pointerEvents: "none",
      }}
    >
      <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6, display: "block" }} />
    </div>,
    document.body
  );
};

const money = { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

/**
 * @param {object}   props.order      the order document
 * @param {string}  [props.currency]  the word in the money headers (default "Ush")
 * @param {function}[props.lineExtra] (item) => node, rendered after the name
 * @param {string}  [props.placeholderImage] shown when a line has no photo
 *                                   (a restaurant passes its dish placeholder)
 */
const OrderLinesTable = ({ order, currency = "Ush", lineExtra, placeholderImage = PLACEHOLDER_IMAGE }) => {
  const items = Array.isArray(order?.orderItems) ? order.orderItems : [];
  const groupMembers = new Set(items.filter((item) => item?.groupMember?.label).map((item) => String(item.groupMember.memberId?._id || item.groupMember.memberId || item.groupMember.label)));
  const isGroup = Boolean(order?.groupBasketId || groupMembers.size);
  const [preview, setPreview] = useState(null); // { src, anchor }

  if (!items.length) {
    return (
      <div className="rod-hint" style={{ padding: "14px 0" }}>
        This order has no items.
      </div>
    );
  }

  return (
    /* Sized to fit the half-width column it usually sits in (about 650px):
       compact cells, a 40px thumbnail, "Qty", and the item column taking
       whatever is left, so a long name wraps to two lines instead of the
       Total column leaving the frame. If it still cannot fit, the frame
       scrolls sideways rather than clipping. */
    <div>
      {isGroup && <div className="lk-group-order-summary" role="note">
        <strong>Order together{groupMembers.size ? ` · ${groupMembers.size} ${groupMembers.size === 1 ? "person" : "people"} with items` : ""}</strong>
        <span>Each item is labelled with who chose it. Pack items by person for one shared delivery.</span>
      </div>}
    <div className="lk-table-frame lk-table-frame--scroll">
      <Table hover className="lk-table lk-table--compact" style={{ tableLayout: "auto" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "center", width: 36 }}>#</th>
            <th style={{ width: 56, textAlign: "left" }}>Image</th>
            <th style={{ width: "100%", textAlign: "left" }}>Item</th>
            <th style={{ textAlign: "center", width: 56 }}>Qty</th>
            <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Price ({currency})</th>
            <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Total ({currency})</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const qty = qtyOf(item);
            const unitPrice = Number(item?.lineUnitPrice ?? item?.price) || 0;
            const lineTotal = Number(item?.lineTotal ?? unitPrice * qty) || 0;
            const name = nameOf(item);
            return (
              <tr key={item?.lineId || item?.id || item?._id?.toString?.() || index}>
                <td style={{ textAlign: "center" }}>{index + 1}</td>
                <td>
                  <img
                    src={imageOf(item, placeholderImage)}
                    alt={name}
                    style={thumb}
                    onError={(e) => {
                      e.target.src = placeholderImage;
                    }}
                    onMouseEnter={(e) =>
                      setPreview({ src: e.currentTarget.currentSrc || e.currentTarget.src, anchor: e.currentTarget.getBoundingClientRect() })
                    }
                    onMouseLeave={() => setPreview(null)}
                  />
                </td>
                <td>
                  <div>
                    {name}
                    {/* Order together: the group member who chose this line. */}
                    {item?.groupMember?.label ? <LineTag tone="info">For {item.groupMember.label}</LineTag> : null}
                  </div>
                  <SpecChips item={item} />
                  {lineExtra ? lineExtra(item) : null}
                </td>
                <td style={{ textAlign: "center" }}>{qty}</td>
                <td style={money}>{formatAmount(unitPrice)}</td>
                <td style={{ ...money, fontWeight: 500 }}>{formatAmount(lineTotal)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <HoverPreview src={preview?.src} anchor={preview?.anchor} />
    </div>
    </div>
  );
};

export default OrderLinesTable;
